import { useState, useEffect, useMemo, useCallback } from "react";
import { Search, BookOpen, Heart, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { VowCard } from "@/components/VowCard";
import { VowDetailDialog } from "@/components/VowDetailDialog";
import { useFavoritesStore } from "@/stores/useFavoritesStore";
import { seedVowsIfNeeded } from "@/lib/db";
import { cn } from "@/lib/utils";
import {
  ALL_VOWS,
  VOW_CATEGORIES,
  type Vow,
  type VowCategory,
} from "@/data/vows";

type FilterTab = "alle" | VowCategory;

const FILTER_EMOJI: Record<FilterTab, string> = {
  alle: "📿",
  "refuge-commitments": "🙏",
  "ten-virtues": "✨",
  "bodhisattva-root": "💛",
  "bodhisattva-secondary": "💜",
  "bodhisattva-helping": "🩷",
  "bodhisattva-enlightenment": "🌟",
  "bodhisattva-black-white": "⚖️",
  "lay-vows": "🏠",
};

const FILTER_ITEMS: { id: FilterTab; label: string; count: number }[] = [
  { id: "alle", label: "Alle", count: ALL_VOWS.length },
  ...VOW_CATEGORIES.map((cat) => ({
    id: cat.id as FilterTab,
    label: cat.labelShort,
    count: cat.count,
  })),
];

export function VowsPage() {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<FilterTab>("alle");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedVow, setSelectedVow] = useState<Vow | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const {
    favoriteIds,
    loaded,
    loadFavorites,
    toggleFavorite,
    isFavorite,
    toggleCategoryFavorite,
    isCategoryFavorite,
  } = useFavoritesStore();

  // Initialize DB and load favorites
  useEffect(() => {
    async function init() {
      await seedVowsIfNeeded();
      await loadFavorites();
      setLoading(false);
    }
    init();
  }, [loadFavorites]);

  // Filter logic
  const filteredVows = useMemo(() => {
    let result = ALL_VOWS;

    // Category filter
    if (activeTab !== "alle") {
      result = result.filter((v) => v.category === activeTab);
    }

    // Favorites filter
    if (showFavoritesOnly) {
      result = result.filter((v) => favoriteIds.has(v.id));
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (v) =>
          v.de.title.toLowerCase().includes(q) ||
          v.de.description.toLowerCase().includes(q) ||
          (v.titleSanskrit && v.titleSanskrit.toLowerCase().includes(q)) ||
          (v.de.positiveFormulation && v.de.positiveFormulation.toLowerCase().includes(q))
      );
    }

    return result;
  }, [activeTab, showFavoritesOnly, searchQuery, favoriteIds]);

  const handleCardClick = useCallback((vow: Vow) => {
    setSelectedVow(vow);
    setDetailOpen(true);
  }, []);

  const handleToggleFavorite = useCallback(
    (vowId: number) => {
      toggleFavorite(vowId);
    },
    [toggleFavorite]
  );

  if (loading || !loaded) {
    return (
      <div className="px-4 py-6 space-y-6">
        <div className="space-y-1">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="grid gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-5">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-vajra-cyan" />
          <h2 className="text-2xl font-bold tracking-tight">
            Gelübde-Bibliothek
          </h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {ALL_VOWS.length} Gelübde und Tugenden zum Durchsuchen
        </p>
      </div>

      {/* Search + Favorites Toggle */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Gelübde suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 border-vajra-navy/50 focus:border-vajra-blue"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Suche leeren"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          variant={showFavoritesOnly ? "default" : "outline"}
          size="icon"
          className={
            showFavoritesOnly
              ? "bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30"
              : "border-vajra-navy/50"
          }
          onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          aria-label={showFavoritesOnly ? "Alle anzeigen" : "Nur Favoriten anzeigen"}
        >
          <Heart
            className={
              showFavoritesOnly
                ? "h-4 w-4 fill-red-500 text-red-500"
                : "h-4 w-4"
            }
          />
        </Button>
      </div>

      {/* Category Filter — horizontally scrollable pills */}
      <div className="-mx-4">
        <div className="flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-none">
          {FILTER_ITEMS.map((item) => {
            const isActive = activeTab === item.id;
            const isCatFav = item.id !== "alle" && isCategoryFavorite(item.id as VowCategory);
            return (
              <div key={item.id} className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all",
                    isActive
                      ? "bg-vajra-blue border-vajra-blue text-white shadow-sm shadow-vajra-blue/25"
                      : "bg-transparent border-gray-200 dark:border-[#1E3A5F] text-gray-600 dark:text-muted-foreground hover:border-vajra-blue/50 hover:text-vajra-blue dark:hover:text-vajra-cyan"
                  )}
                >
                  <span>{FILTER_EMOJI[item.id]}</span>
                  {item.label}
                  <span
                    className={cn(
                      "flex h-4.5 min-w-[1.125rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none",
                      isActive
                        ? "bg-white/25 text-white"
                        : "bg-gray-100 dark:bg-[#1E3A5F] text-gray-500 dark:text-muted-foreground"
                    )}
                  >
                    {item.count}
                  </span>
                </button>
                {item.id !== "alle" && (
                  <button
                    type="button"
                    onClick={() => toggleCategoryFavorite(item.id as VowCategory)}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 dark:border-[#1E3A5F] text-gray-400 hover:border-red-400/60 hover:text-red-400 transition-colors"
                    aria-label={isCatFav ? "Kategorie aus Favoriten entfernen" : "Kategorie als Favorit markieren"}
                  >
                    <Heart
                      className={cn(
                        "h-3.5 w-3.5",
                        isCatFav ? "fill-red-500 text-red-500" : ""
                      )}
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {filteredVows.length}{" "}
          {filteredVows.length === 1 ? "Ergebnis" : "Ergebnisse"}
          {showFavoritesOnly && " (nur Favoriten)"}
          {searchQuery && ` für "${searchQuery}"`}
        </p>
      </div>

      {/* Empty State */}
      {filteredVows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 dark:border-[#1E3A5F] p-8 text-center">
          <Search className="mx-auto h-10 w-10 text-muted-foreground/30" />
          <p className="mt-3 text-sm font-medium text-muted-foreground">
            Keine Ergebnisse gefunden
          </p>
          <p className="mt-1 text-xs text-muted-foreground/60">
            {showFavoritesOnly
              ? "Du hast noch keine Favoriten in dieser Kategorie."
              : "Versuche einen anderen Suchbegriff oder wechsle die Kategorie."}
          </p>
          {(searchQuery || showFavoritesOnly) && (
            <div className="flex justify-center gap-2 mt-4">
              {searchQuery && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchQuery("")}
                  className="text-xs border-gray-200 dark:border-[#1E3A5F]"
                >
                  Suche leeren
                </Button>
              )}
              {showFavoritesOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFavoritesOnly(false)}
                  className="text-xs border-gray-200 dark:border-[#1E3A5F]"
                >
                  Alle anzeigen
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filteredVows.map((vow) => (
            <VowCard
              key={vow.id}
              vow={vow}
              isFavorite={isFavorite(vow.id)}
              onToggleFavorite={handleToggleFavorite}
              onClick={handleCardClick}
              showCategory={activeTab === "alle"}
            />
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <VowDetailDialog
        vow={selectedVow}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        isFavorite={selectedVow ? isFavorite(selectedVow.id) : false}
        onToggleFavorite={handleToggleFavorite}
      />
    </div>
  );
}
